// PRO Portfolio Drift & Rebalancing Alpha Auditor -- TanStack Query hook for the
// synchronous PRO audit endpoint (POST /api/v1/portfolio-drift-auditor-pro/audit).
//
// This is a PROTECTED dashboard tool, so we use the shared authenticated
// apiClient (Bearer token + 401 bounce). The audit runs 6 Numba backtests, a
// 27-policy AI regime search and an 8-point corridor sweep server-side, so we
// override the per-request timeout to give a cold JIT compile plenty of
// headroom over apiClient's default.

import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { apiClient } from '../../api/client';
import type { CoverageResponse, ProDriftAuditRequest, ProDriftAuditResponse } from './types';

const BASE = '/portfolio-drift-auditor-pro';
const AUDIT_TIMEOUT_MS = 45_000;

const toError = (error: unknown, fallback: string): Error => {
    if (axios.isAxiosError(error)) {
        // Backend envelope (app/core/exceptions.py): { status, message, data }.
        const body = error.response?.data as
            | { detail?: unknown; message?: unknown; data?: unknown }
            | undefined;
        const fieldErrors = Array.isArray(body?.data) ? body?.data : body?.detail;
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            const first = fieldErrors[0] as { msg?: string };
            if (first?.msg) return new Error(first.msg.replace(/^Value error, /, ''));
        }
        if (typeof body?.detail === 'string') return new Error(body.detail);
        if (typeof body?.message === 'string' && error.response?.status !== 500) {
            return new Error(body.message);
        }
        return new Error(fallback);
    }
    return error instanceof Error ? error : new Error(fallback);
};

export const useProDriftAudit = () =>
    useMutation<ProDriftAuditResponse, Error, ProDriftAuditRequest>({
        mutationFn: async (payload) => {
            try {
                const { data } = await apiClient.post<ProDriftAuditResponse>(
                    `${BASE}/audit`,
                    payload,
                    { timeout: AUDIT_TIMEOUT_MS },
                );
                return data;
            } catch (error) {
                throw toError(error, 'Could not run the PRO drift audit. Please try again.');
            }
        },
    });

export const fetchProDriftCoverage = async (tickers: string[]): Promise<CoverageResponse> => {
    const { data } = await apiClient.post<CoverageResponse>(`${BASE}/coverage`, { tickers });
    return data;
};

// Probes how much overlapping price history the local store holds for the
// current tickers, so the form can offer only the lookback windows we can serve.
export const useProDriftCoverage = (tickers: string[]) =>
    useQuery<CoverageResponse>({
        queryKey: ['pro-drift-coverage', [...tickers].sort().join(',')],
        queryFn: () => fetchProDriftCoverage(tickers),
        enabled: tickers.length > 0,
        staleTime: 5 * 60_000,
        retry: false,
    });
